import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Switch} from '../../buttons/Swtich';
import {anchorAttr} from '../../../guides/anchors';
import {ArrowUUpLeft, CaretDown, CaretRight, Copy, EnvelopeSimple, PencilSimple, Rows, Trash, User, Warning} from '../../icons';
import {elapsedSince} from '../../agency/v2/AlertsPopover';
import {RuleEffects} from './RuleEffects';
import {RowMenu} from './RowMenu';
import {
    availableChannels,
    CHANNEL_EMAIL,
    channelsLine,
    createdByOf,
    draftFromRule,
    formatThreshold,
    hiddenScopeCount,
    isActiveRule,
    isControlRule,
    isExceptionRule,
    isOwnRule,
    isRuleOn,
    isSystemRule,
    namesText,
    PAUSED_CREATOR_LEFT,
    PAUSED_LIMIT,
    paraLine,
    SCOPE_AGENCY,
    SCOPE_ANY,
    SCOPE_CLIENT,
    SCOPE_LABEL,
    SCOPE_LINE,
    teamActionOf,
} from '../rulesModel';
import {
    alertSentence,
    alertTitle,
    controlScopeText,
    customRuleTokens,
    exceptionSentence,
    exceptionTitle,
    ruleWarning,
    systemRuleTitle,
    thresholdsSummary,
} from '../ruleText';
import {
    duplicateRulePath,
    lineAlertsConfigPath,
    NOW_VIEW_AVAILABLE,
    ruleAlertsPath,
    rulePath,
} from '../alertsCenterPaths';

/** La frase de corrido, con los valores elegidos marcados como chips de sólo lectura. */
export function SentenceLine({tokens, className = ''}) {
    return (
        <p className={`alc-sentence -static ${className}`.trim()}>
            {tokens.map((token, index) => (token.type === 'chip'
                ? <span key={index} className={`alc-chip -static${token.empty ? ' -empty' : ''}`}>{token.text}</span>
                : <span key={index} className="alc-sentence__word">{token.text}</span>))}
        </p>
    );
}

function OwnConfigLines({lines}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    if (!lines || lines.length === 0) return null;
    return (
        <div className="alc-own">
            <button type="button" className="alc-own__toggle" aria-expanded={open}
                    onClick={() => setOpen((value) => !value)}>
                {open ? <CaretDown size={12} weight="bold"/> : <CaretRight size={12} weight="bold"/>}
                {t('alerts_center.rules.own_config', {count: lines.length})}
            </button>
            {open && (
                <ul className="alc-own__list">
                    {lines.map((line) => (
                        <li key={line.business_unit_id}>
                            <Link to={lineAlertsConfigPath(line.business_unit_id)} className="alc-link">
                                {line.client_name ? `${line.client_name} · ` : ''}{line.business_unit_name}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/**
 * Las anclas de la guía `aprende-alertas` de Aprende (D7 de la tanda del 26/10): la fila y su «Editar», sólo en la
 * PRIMERA alerta de la lista (`guideRow`, lo decide la lista: el runner toma el primer elemento marcado del DOM), y el
 * «Editar» sólo si quien mira puede editarla (si no, el botón dice «Ver» y el paso opcional se saltea).
 */
function guideAnchors(guideRow, canEdit) {
    return {
        row: guideRow ? anchorAttr('alerts_rules_first_row') : {},
        edit: guideRow && canEdit ? anchorAttr('alerts_rules_first_edit') : {},
    };
}

/**
 * Una regla de Dashbo (las sensibilidades de siempre): título, umbrales, qué hace y por dónde avisa.
 * Tocarla abre el panel con sus umbrales; el interruptor apaga color y aviso juntos, en el acto.
 */
export function SystemRuleRow({rule, effects, anchorId, highlighted, busy, readOnly = false, guideRow = false, onToggle,
                                  onOpen}) {
    const {t, i18n} = useTranslation();
    const {title, subtitle} = systemRuleTitle(rule, t);
    const active = rule.is_active !== false;
    const titleId = `${anchorId}-title`;
    const guide = guideAnchors(guideRow, !readOnly);
    return (
        <article id={anchorId} data-rule-id={rule.id} {...guide.row}
                 className={`alc-rule -system${active ? '' : ' -paused'}${highlighted ? ' -highlight' : ''}`}>
            <div className="alc-rule__switch">
                <Switch name={`rule-${anchorId}`} className="alc-switch" checked={active} disabled={busy || readOnly}
                        aria-labelledby={titleId}
                        onChange={(event) => onToggle(rule, event.target.checked)}/>
            </div>
            <div className="alc-rule__body">
                <div className="alc-rule__top">
                    <span className="alc-kind -system">{t('alerts_center.rules.kind_system')}</span>
                    <button type="button" id={titleId} className="alc-rule__title -button" onClick={() => onOpen(rule)}>
                        {title}
                        {subtitle && <span className="alc-rule__subtitle">{subtitle}</span>}
                    </button>
                </div>
                <p className="alc-rule__thresholds">{thresholdsSummary(rule, t, i18n.language)}</p>
                <RuleEffects effects={effects}/>
                <div className="alc-rule__meta">
                    <span>{t('alerts_center.rules.scope_all_lines')}</span>
                </div>
                <OwnConfigLines lines={rule.lines_with_own_config}/>
            </div>
            <div className="alc-rule__acts">
                <button type="button" className="alc-btn -ghost" onClick={() => onOpen(rule)} {...guide.edit}>
                    <PencilSimple size={13} weight="bold"/>
                    {readOnly ? t('alerts_center.rules.action_see_thresholds') : t('alerts_center.rules.action_edit_thresholds')}
                </button>
            </div>
        </article>
    );
}

function customStats(rule, t) {
    const stats = rule.stats || {};
    const parts = [];
    if (rule.paused_reason === PAUSED_CREATOR_LEFT) {
        parts.push({key: 'paused', text: t('alerts_center.rules.paused_creator_left'), tone: 'warn'});
    } else if (rule.paused_reason === PAUSED_LIMIT) {
        // En pausa porque la agencia está en el tope de reglas activas (C-ac-fe-centro-07).
        parts.push({key: 'paused', text: t('alerts_center.rules.paused_limit'), tone: 'warn'});
    }
    if (stats.active_now > 0) {
        parts.push({key: 'now', text: t('alerts_center.rules.active_now', {count: stats.active_now}), tone: 'alert'});
    }
    parts.push({
        key: 'last',
        text: stats.last_triggered_on
            ? t('alerts_center.rules.last_triggered', {when: t('alerts_center.rules.ago', {elapsed: elapsedSince(stats.last_triggered_on, t)})})
            : t('alerts_center.rules.never_triggered'),
    });
    if (stats.triggered_30d > 0) {
        parts.push({key: '30d', text: t('alerts_center.rules.triggered_30d', {count: stats.triggered_30d})});
    }
    if (typeof stats.lines_in_scope === 'number') {
        parts.push({key: 'lines', text: t('alerts_center.rules.lines_in_scope', {count: stats.lines_in_scope})});
    }
    return parts;
}

/**
 * Una regla personalizada: su frase, qué hace, cuándo disparó y las acciones. Pausar y reanudar es el
 * interruptor (en la fila no hay un botón aparte para lo mismo); editar, duplicar y eliminar con
 * deshacer.
 */
export function CustomRuleRow({rule, effects, anchorId, highlighted, busy, readOnly = false, guideRow = false, onToggle,
                                  onDelete}) {
    const {t, i18n} = useTranslation();
    // Con un motivo de pausa, pausada aunque el flag diga otra cosa (C-ac-fe-centro-08).
    const active = isActiveRule(rule);
    // Sin quien la creó, reanudarla no hace nada: el interruptor no se ofrece.
    const creatorLeft = rule.paused_reason === PAUSED_CREATOR_LEFT;
    const titleId = `${anchorId}-title`;
    const tokens = customRuleTokens(rule, draftFromRule(rule, {locale: i18n.language}), {t, locale: i18n.language});
    const createdBy = rule.created_by && rule.created_by.name;
    const guide = guideAnchors(guideRow, !readOnly);
    return (
        <article id={anchorId} data-rule-id={rule.id} {...guide.row}
                 className={`alc-rule -custom${active ? '' : ' -paused'}${highlighted ? ' -highlight' : ''}`}>
            <div className="alc-rule__switch">
                <Switch name={`rule-${anchorId}`} className="alc-switch" checked={active}
                        disabled={busy || readOnly || creatorLeft}
                        aria-labelledby={titleId}
                        onChange={(event) => onToggle(rule, event.target.checked)}/>
            </div>
            <div className="alc-rule__body">
                <div className="alc-rule__top">
                    <span className="alc-kind -custom">{t('alerts_center.rules.kind_custom')}</span>
                    <Link id={titleId} to={rulePath(rule.id)} className="alc-rule__title">{rule.name}</Link>
                </div>
                <SentenceLine tokens={tokens}/>
                <RuleEffects effects={effects}/>
                <div className="alc-rule__meta">
                    {customStats(rule, t).map((part) => (
                        <span key={part.key} className={part.tone ? `-${part.tone}` : undefined}>{part.text}</span>
                    ))}
                    {createdBy && <span>{t('alerts_center.rules.created_by', {name: createdBy, interpolation: {escapeValue: false}})}</span>}
                </div>
            </div>
            <div className="alc-rule__acts">
                <Link to={rulePath(rule.id)} className="alc-btn -ghost" {...guide.edit}>
                    <PencilSimple size={13} weight="bold"/>
                    {readOnly ? t('alerts_center.rules.action_see') : t('alerts_center.rules.action_edit')}
                </Link>
                {!readOnly && (
                    <Link to={duplicateRulePath(rule.id)} className="alc-btn -ghost">
                        <Copy size={13} weight="bold"/>
                        {t('alerts_center.rules.action_duplicate')}
                    </Link>
                )}
                {NOW_VIEW_AVAILABLE && (
                    <Link to={ruleAlertsPath(rule.id)} className="alc-btn -ghost">
                        {t('alerts_center.rules.action_see_alerts')}
                    </Link>
                )}
                {!readOnly && (
                    <button type="button" className="alc-btn -ghost -danger" disabled={busy}
                            onClick={() => onDelete(rule)}>
                        <Trash size={13} weight="bold"/>
                        {t('alerts_center.rules.action_delete')}
                    </button>
                )}
            </div>
        </article>
    );
}

// ------------------------------------------------------------ v2: «Mis reglas» (UD-18, 24/10) --------------

const SCOPE_IN_KEYS = {
    [SCOPE_LINE]: 'alerts_center.row.scope_in_lines',
    [SCOPE_CLIENT]: 'alerts_center.row.scope_in_clients',
    [SCOPE_LABEL]: 'alerts_center.row.scope_in_labels',
};

/**
 * En qué líneas avisa (ronda 5, README v2 §4.8): «En todas las líneas (62)», «En 1 línea: Todo Facebook (Gaby Corp)»,
 * «En 3 clientes: …». Un aviso de control mira lo que mira su aviso y se dice fijo («En todas las cuentas»). Con
 * líneas que hoy no se miden (`scope.inactive_count`, una en pausa o de un cliente inactivo), se dice cuántas
 * (QA-VIS-av2c-01).
 */
export function scopeLine(rule, t) {
    if (isControlRule(rule)) return controlScopeText(rule, t);
    const scope = rule.scope || {};
    const stats = rule.stats || {};
    const count = typeof stats.lines_in_scope === 'number' ? stats.lines_in_scope : null;
    if (scope.type === SCOPE_AGENCY || scope.type === SCOPE_ANY) {
        const key = scope.type === SCOPE_AGENCY ? 'alerts_center.row.scope_all' : 'alerts_center.row.scope_any';
        return count !== null ? t(`${key}_count`, {count}) : t(key);
    }
    const key = SCOPE_IN_KEYS[scope.type];
    if (!key) return null;
    const resolved = Array.isArray(scope.resolved) ? scope.resolved : [];
    const names = resolved
        .map((item) => (scope.type === SCOPE_LINE && item.client_name ? `${item.name} (${item.client_name})` : item.name))
        .filter(Boolean);
    const inactive = typeof scope.inactive_count === 'number' ? scope.inactive_count : 0;
    if (scope.type === SCOPE_LINE && count === 0 && !names.length && inactive > 0) {
        return t('alerts_center.row.scope_inactive', {count: inactive});
    }
    const total = Array.isArray(scope.ids) && scope.ids.length ? scope.ids.length : names.length + hiddenScopeCount(rule);
    const base = t(key, {count: total, names: namesText(names, t, {more: hiddenScopeCount(rule)}),
        interpolation: {escapeValue: false}});
    return inactive > 0 ? `${base} · ${t('alerts_center.row.scope_inactive_extra', {count: inactive})}` : base;
}

/** El chip junto al título: «Dashbo», «Tuya» o, en una propia de otra persona, de quién es. */
export function ruleBadge(rule, viewerId, t) {
    if (isSystemRule(rule)) return {text: t('alerts_center.mr.badge_dashbo'), mine: false};
    if (isOwnRule(rule, viewerId)) return {text: t('alerts_center.mr.badge_mine'), mine: true};
    const creator = createdByOf(rule);
    return {
        text: creator.name
            ? t('alerts_center.mr.badge_other', {name: creator.name, interpolation: {escapeValue: false}})
            : t('alerts_center.mr.badge_other_unknown'),
        mine: false,
    };
}

/** Las partes de la frase de una fila: `{pre, value, post}`, con el número aparte para resaltarlo. */
function sentenceParts(rule, {exception, parent, t, locale}) {
    if (exception) return exceptionSentence(rule, parent, {t, locale});
    const {pre, post, unit} = alertSentence(rule, {t, locale});
    return {pre, post, value: formatThreshold(rule.condition && rule.condition.value, unit, locale)};
}

/**
 * Una fila de «Mis reglas» (UD-18, el mockup aprobado el 24/10):
 *
 * - El título con su chip («Dashbo», «Tuya»); la frase con el número; un renglón con íconos: para quién, por dónde y,
 *   en una de Dashbo que no es de toda la agencia, en qué líneas; una advertencia corta en ámbar si no avisa aunque se
 *   vea activa; y la actividad («3 vigentes ahora», «52 avisos en 30 días · Ver qué disparó») sólo si hay.
 * - A la derecha, «Activa» o «Pausada» con el switch (`is_active`, con `can_pause`), «Editar» (el constructor; «Ver»
 *   para quien no la puede cambiar) y «⋯» con lo que se usa poco: «Dejar de recibirla» / «Volver a recibirla» (D19,
 *   sólo en las de equipo; con el filtro de otra persona no se ofrece, el estado que viene es el de ella), «Volver al
 *   número de la agencia» (una excepción, con `can_delete`), «Duplicar» y «Eliminar» (las propias) y «Ver qué disparó».
 * - Una excepción (`exception`, la de Dashbo para unas líneas: `BUDGET_1@90`) cuelga de su alerta (`parent`): se titula
 *   por sus líneas y su frase se lee contra la de la agencia.
 * - `context`: la alerta de una excepción que pasó el filtro, atenuada (la excepción no se lee sola).
 * - `notice`: lo que va pegado debajo de la fila (la pregunta de `?pausar=1`).
 * - `guideRow`: es la primera de la lista y lleva las anclas de la guía `aprende-alertas` (`guideAnchors`).
 */
export function AlertRowV2({rule, anchorId, viewerId, canEditPage = true, busy = false, fresh = false, highlighted = false,
                               notice = null, team = null, person = null, exception = false, parent = null, parentNote = null,
                               orphan = false, context = false, seeAlertsOnSystem = false, guideRow = false, onToggleActive,
                               onOptOut, onReturnToAgency = null, onRemove = null}) {
    const {t, i18n} = useTranslation();
    const system = isSystemRule(rule);
    const canEdit = canEditPage && rule.can_edit !== false;
    const guide = guideAnchors(guideRow, canEdit);
    const canPause = canEditPage && (typeof rule.can_pause === 'boolean' ? rule.can_pause : canEdit);
    const on = isRuleOn(rule);
    const toggleable = canPause && rule.paused_reason !== PAUSED_CREATOR_LEFT && Boolean(onToggleActive);
    const titleId = `${anchorId}-title`;
    const stateId = `${anchorId}-state`;
    const title = exception ? exceptionTitle(rule, t) : alertTitle(rule, t);
    const badge = exception ? null : ruleBadge(rule, viewerId, t);
    // «Para» dice a quién le llega el correo: un aviso que no tiene correo no lo lleva.
    const para = availableChannels(rule).includes(CHANNEL_EMAIL) ? paraLine(rule, {t, viewerId, team, person}) : null;
    const action = person ? null : teamActionOf(rule);
    // Por la clave y no por dónde se dibuja: una excepción huérfana (sin su alerta en la lista) también vuelve.
    const returnable = canEditPage && isExceptionRule(rule) && rule.can_delete === true && Boolean(onReturnToAgency);
    const removable = canEditPage && !system && rule.can_delete === true && Boolean(onRemove);
    const warning = ruleWarning(rule, t);
    const stats = rule.stats || {};
    const activeNow = stats.active_now > 0 ? stats.active_now : 0;
    const triggered = stats.triggered_30d > 0 ? stats.triggered_30d : 0;
    // En qué líneas: lo dice el grupo en las de toda la agencia y la frase en las propias («cuando el cliente Dorfman»);
    // en una de Dashbo con otro alcance, no lo dice nadie más.
    const scopeType = rule.scope && rule.scope.type;
    const showScope = !exception && (orphan || (system && scopeType !== SCOPE_AGENCY && scopeType !== SCOPE_ANY));
    const scope = showScope ? scopeLine(rule, t) : null;
    // «Ver qué disparó» filtra «Ahora» por la regla, y «Ahora» reconoce sólo las alertas de una propia (su indicador es
    // el de las reglas, `nowLogic.ruleIdOf`; el backend cuenta igual en `chips_by_rule`): las de Dashbo convertidas
    // escriben con su indicador de siempre, y el link abriría una bandeja vacía. Verificado el 24/10 (P-4 de la tanda).
    const seeAlerts = NOW_VIEW_AVAILABLE && (!system || seeAlertsOnSystem);
    const parts = sentenceParts(rule, {exception, parent, t, locale: i18n.language});

    const menu = [];
    if (action) {
        menu.push({key: 'team', label: t(action.actionKey), onSelect: () => onOptOut(rule, action.next),
            icon: action.next ? null : <ArrowUUpLeft size={15}/>});
    }
    if (returnable) {
        menu.push({key: 'return', label: t('alerts_center.row.return_to_agency'), onSelect: () => onReturnToAgency(rule),
            icon: <Rows size={15}/>});
    }
    if (!system && canEditPage) {
        menu.push({key: 'duplicate', label: t('alerts_center.rules.action_duplicate'), to: duplicateRulePath(rule.id),
            icon: <Copy size={15}/>});
    }
    if (seeAlerts) {
        menu.push(activeNow > 0 || triggered > 0
            ? {key: 'alerts', label: t('alerts_center.rules.action_see_alerts'), to: ruleAlertsPath(rule.id),
                separator: menu.length > 0}
            : {key: 'alerts', label: t('alerts_center.rules.action_see_alerts'), disabled: true,
                hint: t('alerts_center.mr.no_alerts_hint'), separator: menu.length > 0});
    }
    if (removable) {
        menu.push({key: 'delete', label: t('alerts_center.rules.action_delete'), onSelect: () => onRemove(rule), danger: true,
            icon: <Trash size={15}/>, separator: true});
    }
    // Mientras la fila escribe, lo que escribe no se ofrece: los links siguen.
    const menuItems = busy ? menu.map((item) => (item.onSelect ? {...item, disabled: true} : item)) : menu;

    const className = ['alc-mr-row', on ? '' : '-paused', exception ? '-exc' : '', context ? '-context' : '',
        fresh ? '-fresh' : '', highlighted ? '-highlight' : ''].filter(Boolean).join(' ');

    return (
        <article id={anchorId} data-rule-id={rule.id} aria-labelledby={titleId} data-on={String(on)} className={className}
                 {...guide.row}>
            <div className="alc-mr-row__main">
                <div className="alc-mr-row__title">
                    <h4 id={titleId} className="alc-mr-row__name">{title}</h4>
                    {badge && <span className={`alc-badge${badge.mine ? ' -mine' : ''}`} data-testid="alc-row-badge">{badge.text}</span>}
                    {fresh && <span className="alc-v2-tag -fresh" data-testid="alc-fresh">{t('alerts_center.row.fresh')}</span>}
                </div>
                <p className="alc-mr-row__sentence">
                    {parts.pre}
                    {parts.value && <strong className="alc-num -static" data-testid="alc-num">{parts.value}</strong>}
                    {parts.post}
                </p>
                <div className="alc-mr-row__meta" data-testid="alc-row-facts">
                    {scope && (
                        <span className="alc-mi" data-testid="alc-row-scope">
                            <Rows size={15} aria-hidden="true"/>{scope}
                        </span>
                    )}
                    {para && (
                        <span className="alc-mi" data-testid="alc-row-para">
                            <User size={15} aria-hidden="true"/>
                            <span>{t('alerts_center.row.para')} <strong>{para}</strong></span>
                        </span>
                    )}
                    <span className="alc-mi" data-testid="alc-row-channels">
                        <EnvelopeSimple size={15} aria-hidden="true"/>{channelsLine(rule, t)}
                    </span>
                    {parentNote && <span className="alc-mr-row__note" data-testid="alc-row-parent-note">{parentNote}</span>}
                    {warning && (
                        <span className="alc-flag -warn" data-testid="alc-row-warning">
                            <Warning size={13} weight="bold" aria-hidden="true"/>{warning}
                        </span>
                    )}
                    {activeNow > 0 && (
                        <span className="alc-flag -live" data-testid="alc-row-now">{t('alerts_center.rules.active_now', {count: activeNow})}</span>
                    )}
                    {triggered > 0 && (
                        <span data-testid="alc-row-activity">
                            {t('alerts_center.row.triggered_30d', {count: triggered})}
                            {seeAlerts && (
                                <>
                                    {' · '}
                                    <Link to={ruleAlertsPath(rule.id)} className="alc-link">{t('alerts_center.rules.action_see_alerts')}</Link>
                                </>
                            )}
                        </span>
                    )}
                </div>
            </div>
            <div className="alc-mr-row__acts">
                <span id={stateId} className={`alc-mr-row__state${on ? ' -on' : ''}`} data-testid="alc-row-state">
                    {t(on ? 'alerts_center.row.state_active' : 'alerts_center.row.state_paused')}
                </span>
                {toggleable && (
                    <span className="alc-mr-row__switch" data-testid="alc-row-toggle">
                        <Switch name={`${anchorId}-active`} className="alc-switch" checked={on} disabled={busy}
                                aria-labelledby={`${titleId} ${stateId}`}
                                onChange={(event) => onToggleActive(rule, event.target.checked)}/>
                    </span>
                )}
                <Link to={rulePath(rule.id)} className="alc-btn -ghost -sm" data-testid="alc-row-edit" {...guide.edit}>
                    {canEdit ? t('alerts_center.row.edit') : t('alerts_center.rules.action_see')}
                </Link>
                <RowMenu items={menuItems} label={t('alerts_center.mr.more_actions')} reserve/>
            </div>
            {notice}
        </article>
    );
}
